import { easeCubicOut } from 'd3-ease';
import { select } from 'd3-selection';

import type { BubblePos, BubblePosCache, PackLayoutNode, PackNode } from '../../../lib/bubble/packNodeTypes';
import type {
  CircleNodeSelection,
  LabelGroupSelection,
} from '../../../lib/d3/d3SelectionTypes';
import type { ExplorationVolume } from '../../../types/exploration';
import { managedInnerRatio } from '../../../lib/bubble/bubbleHelpers';
import { bubbleNodeKey } from './bubbleNodeKey';
import { bubbleTransitionMs } from './bubbleRenderOptions';

export function isFiniteBubblePos(pos: BubblePos | null | undefined): pos is BubblePos {
  return (
    pos != null &&
    Number.isFinite(pos.cx) &&
    Number.isFinite(pos.cy) &&
    Number.isFinite(pos.r)
  );
}

export function readPackNodePos(d: PackNode | null | undefined): BubblePos | null {
  if (!d) return null;
  if (d.x == null || d.y == null || d.r == null) return null;
  const pos = { cx: d.x, cy: d.y, r: d.r };
  return isFiniteBubblePos(pos) ? pos : null;
}

function packNodePos(d: PackNode): BubblePos {
  return readPackNodePos(d) ?? { cx: 0, cy: 0, r: 0 };
}

export function allNodesHaveFinitePackPos(nodes: PackNode[]): boolean {
  return nodes.length > 0 && nodes.every((d) => readPackNodePos(d) != null);
}

function selectionHasFinitePackPos(selection: CircleNodeSelection): boolean {
  let ok = true;
  selection.each((d) => {
    if (!readPackNodePos(d)) ok = false;
  });
  return ok;
}

function readCirclePos(circle: Element): BubblePos | null {
  const fromDatum = readPackNodePos(select(circle).datum() as PackNode | undefined);
  if (fromDatum) return fromDatum;

  const cx = parseFloat(circle.getAttribute('cx') ?? '');
  const cy = parseFloat(circle.getAttribute('cy') ?? '');
  const r = parseFloat(circle.getAttribute('r') ?? '');
  const pos = { cx, cy, r };
  return isFiniteBubblePos(pos) ? pos : null;
}

export function dropInvalidPosCacheEntries(posCache: BubblePosCache): void {
  posCache.forEach((pos, key) => {
    if (!isFiniteBubblePos(pos)) posCache.delete(key);
  });
}

export function snapshotLiveBubblePositions(
  container: HTMLElement,
  view: string,
  canvasId: string
): { posCache: BubblePosCache; posCacheContext: string | null } {
  const posCache: BubblePosCache = new Map();
  let posCacheContext: string | null = null;
  const liveCircles = container.querySelectorAll(
    '.bubbles-main circle:not(.bubble-halo):not(.bubble-hover-rim)'
  );
  if (liveCircles.length) {
    posCacheContext = `${view}|${canvasId}`;
    liveCircles.forEach((c) => {
      const d = select(c).datum() as PackNode | undefined;
      if (!d?.data) return;
      const pos = readCirclePos(c);
      if (!pos) return;
      posCache.set(bubbleNodeKey(d), pos);
    });
    dropInvalidPosCacheEntries(posCache);
    if (posCache.size === 0) {
      posCacheContext = null;
    }
  }
  return { posCache, posCacheContext };
}

export function prepareBubbleTransition(
  nodes: PackNode[],
  posCache: BubblePosCache,
  posCacheContext: string | null,
  transitionCtx: string
) {
  dropInvalidPosCacheEntries(posCache);

  if (posCache.size === 0) {
    posCacheContext = transitionCtx;
    for (const d of nodes) {
      const target = readPackNodePos(d);
      if (!target) continue;
      posCache.set(bubbleNodeKey(d), { cx: target.cx, cy: target.cy, r: 0 });
    }
  }

  dropInvalidPosCacheEntries(posCache);
  const packLayoutValid = allNodesHaveFinitePackPos(nodes);
  const useTransition =
    packLayoutValid && posCache.size > 0 && posCacheContext === transitionCtx;
  const startPos = (d: PackNode): BubblePos => {
    const target = readPackNodePos(d);
    if (!target) {
      return { cx: 0, cy: 0, r: 0 };
    }
    if (!useTransition) return target;
    const cached = posCache.get(bubbleNodeKey(d));
    if (cached && isFiniteBubblePos(cached)) return cached;
    return { cx: target.cx, cy: target.cy, r: 0 };
  };
  return { useTransition, startPos, posCacheContext };
}

export function bubbleInvertTransform(d: PackNode, posCache: BubblePosCache): string {
  const cached = posCache.get(bubbleNodeKey(d));
  if (!cached || !isFiniteBubblePos(cached)) return 'translate(0,0) scale(0.001)';

  const target = readPackNodePos(d);
  if (!target) return 'translate(0,0) scale(0.001)';

  const dx = cached.cx - target.cx;
  const dy = cached.cy - target.cy;
  const s = target.r > 0 ? Math.max(0.001, cached.r / target.r) : 1;
  const tx = dx + target.cx * (1 - s);
  const ty = dy + target.cy * (1 - s);
  if (!Number.isFinite(tx) || !Number.isFinite(ty) || !Number.isFinite(s)) {
    return 'translate(0,0) scale(0.001)';
  }
  return `translate(${tx},${ty}) scale(${s})`;
}

export function runBubbleFlipTransition(deps: {
  useTransition: boolean;
  posCache: BubblePosCache;
  labelG: LabelGroupSelection;
  bubbleG: CircleNodeSelection;
  haloSel: CircleNodeSelection;
  hoverRimSel: CircleNodeSelection;
  managedSel: CircleNodeSelection;
  currentZoomK: () => number;
  effectiveHaloWidth: (r: number, base: number, k?: number) => number;
  hoverRimRadius: (r: number, base: number, k?: number) => number;
  HALO_WIDTH_LOW: number;
  managedInnerRatio: (v: Pick<ExplorationVolume, 'size' | 'datafyDiskSize'>) => number;
}): ReturnType<typeof setTimeout> | null {
  const {
    useTransition,
    posCache,
    labelG,
    bubbleG,
    haloSel,
    hoverRimSel,
    managedSel,
    currentZoomK,
    effectiveHaloWidth,
    hoverRimRadius,
    HALO_WIDTH_LOW,
    managedInnerRatio: managedInnerRatioFn,
  } = deps;

  if (!useTransition) return null;

  const flipSelections = [bubbleG, haloSel, hoverRimSel, managedSel];
  if (!flipSelections.every((sel) => selectionHasFinitePackPos(sel))) {
    return null;
  }

  labelG.style('display', 'none');

  bubbleG
    .attr('cx', (d) => packNodePos(d).cx)
    .attr('cy', (d) => packNodePos(d).cy)
    .attr('r', (d) => packNodePos(d).r);
  const flipK = currentZoomK();
  haloSel
    .attr('cx', (d) => packNodePos(d).cx)
    .attr('cy', (d) => packNodePos(d).cy)
    .attr('r', (d) => packNodePos(d).r)
    .attr('stroke-width', (d) =>
      effectiveHaloWidth(packNodePos(d).r, HALO_WIDTH_LOW, flipK)
    );
  hoverRimSel
    .attr('cx', (d) => packNodePos(d).cx)
    .attr('cy', (d) => packNodePos(d).cy)
    .attr('r', (d) => hoverRimRadius(packNodePos(d).r, HALO_WIDTH_LOW, flipK));
  managedSel
    .attr('cx', (d) => packNodePos(d).cx)
    .attr('cy', (d) => packNodePos(d).cy)
    .attr('r', (d) =>
      packNodePos(d).r * managedInnerRatioFn(d.data.volume as ExplorationVolume)
    );

  const invertTransform = (d: PackLayoutNode) => bubbleInvertTransform(d, posCache);
  bubbleG.attr('transform', invertTransform);
  haloSel.attr('transform', invertTransform);
  hoverRimSel.attr('transform', invertTransform);
  managedSel.attr('transform', invertTransform);

  const tEase = easeCubicOut;
  const identity = 'translate(0,0) scale(1)';
  bubbleG.transition().duration(bubbleTransitionMs).ease(tEase).attr('transform', identity);
  haloSel.transition().duration(bubbleTransitionMs).ease(tEase).attr('transform', identity);
  hoverRimSel.transition().duration(bubbleTransitionMs).ease(tEase).attr('transform', identity);
  managedSel.transition().duration(bubbleTransitionMs).ease(tEase).attr('transform', identity);
  return setTimeout(() => {
    labelG.style('display', '');
  }, bubbleTransitionMs);
}
